/**
 * Pure helpers that map MIDI events to answer-option IDs for the
 * exercise types the input-midi adapter supports.
 *
 * The dispatch surface mirrors what Practice cares about, NOT one
 * function per exercise type — most types share helpers (e.g. the
 * chord graders both run through `identifyChord` and only differ in
 * the option-ID shape).
 *
 * No DOM, no audio, no Web MIDI APIs. The MidiInput class produces
 * note events; this module decides what they MEAN.
 *
 * Phase 2A C.2 supports:
 *   - scale-degree-recognition  (single noteon)
 *   - interval-recognition      (two consecutive noteons)
 *   - chord-quality-recognition (3-4 simultaneous noteons; triads + 7ths)
 *   - chord-inversion-recognition (3-4 simultaneous noteons; bass disambiguates)
 *   - melodic-dictation         (helper only — D.1 will wire Practice)
 *
 * Out of scope for C.2 (per spec §10.3):
 *   - progression / cadence / mode / secondary-dominant / modulation —
 *     these don't fit MIDI input cleanly. Practice gates them out at the
 *     dispatch level with a "switch input mode" hint; these helpers
 *     return null defensively.
 *   - slash chords — bass-as-non-chord-tone edge case is genuinely hard
 *     to disambiguate without a key context. Documented as follow-up.
 *   - extended chords (9/11/13) and altered dominants — the chord-id
 *     matrix is unambiguous only for triads + 7ths. The chord-quality
 *     grader returns null when the played chord can't be identified.
 */

import type { Exercise } from "@music-app/exercise-generator";
import {
  midiToScaleDegree as theoryMidiToScaleDegree,
  type ScaleDegreeSequence,
} from "@music-app/theory-engine";
import { identifyChord, type IdentifiedChord } from "./chord-id.js";

/* ─── Scale-degree grading ───────────────────────────────────────────── */

export interface MidiScaleDegreeResult {
  optionId: string;
}

/**
 * Map a single MIDI noteon → option ID for a scale-degree exercise.
 *
 * Octave-agnostic. Returns null if the note isn't a diatonic scale tone,
 * or if the exercise meta lacks the tonicMidi/tonalityMode context the
 * scale-degree generator populates.
 */
export function gradeMidiScaleDegree(
  midi: number,
  exercise: Exercise,
): MidiScaleDegreeResult | null {
  if (exercise.exerciseType !== "scale-degree-recognition") return null;
  const tonicMidi = exercise.meta.tonicMidi;
  const mode = exercise.meta.tonalityMode;
  if (typeof tonicMidi !== "number" || (mode !== "major" && mode !== "minor")) {
    return null;
  }
  // Diatonic-only: chromatic notes don't get an option for plain
  // scale-degree recognition. allowChromatic stays false.
  const sd = theoryMidiToScaleDegree(midi, tonicMidi, mode);
  if (sd === null) return null;
  const optionId = `degree-${sd.degree}`;
  // Defensive — the leading-tone toggle can hide degree 7 from the
  // option set even when the user plays it. Don't grade an answer that
  // isn't on the screen.
  if (!exercise.options.some((o) => o.id === optionId)) return null;
  return { optionId };
}

/* ─── Interval grading ───────────────────────────────────────────────── */

/**
 * Diatonic spellings preferred by the interval generator. The tritone
 * has two valid spellings (A4, d5); the table picks A4 to match the
 * generator's pickDistractors output, but the helper below also accepts
 * d5 if the option set uses that spelling.
 */
const PREFERRED_INTERVAL_BY_SEMITONE: Record<number, string> = {
  0: "P1",  1: "m2",  2: "M2",  3: "m3",  4: "M3",
  5: "P4",  6: "A4",  7: "P5",  8: "m6",  9: "M6",
  10: "m7", 11: "M7", 12: "P8",
};

/**
 * Both spellings for each semitone count. The grader prefers the
 * diatonic name but falls back to the alternate if only it is in the
 * option set (e.g. an exercise that asks specifically for d5 vs A4).
 */
const INTERVAL_ALIASES_BY_SEMITONE: Record<number, readonly string[]> = {
  0: ["P1"],
  1: ["m2"],
  2: ["M2"],
  3: ["m3"],
  4: ["M3"],
  5: ["P4"],
  6: ["A4", "d5"],
  7: ["P5"],
  8: ["m6"],
  9: ["M6"],
  10: ["m7"],
  11: ["M7"],
  12: ["P8"],
};

export interface MidiIntervalResult {
  optionId: string;
}

/**
 * Map two MIDI noteons → option ID for an interval exercise.
 *
 * Order-insensitive (ascending and descending sound the same). Compound
 * intervals (e.g. M9) collapse to their simple form (M2) since Phase 2
 * doesn't drill compound intervals as a separate exercise.
 */
export function gradeMidiInterval(
  first: number,
  second: number,
  exercise: Exercise,
): MidiIntervalResult | null {
  if (exercise.exerciseType !== "interval-recognition") return null;
  const semitones = Math.abs(Math.round(second) - Math.round(first));
  // Reduce to [0, 12]. Octave (12) is a legitimate answer; everything
  // larger collapses to its simple form.
  const simple =
    semitones === 12 ? 12 : ((semitones % 12) + 12) % 12;
  const aliases = INTERVAL_ALIASES_BY_SEMITONE[simple];
  if (!aliases) return null;

  // Prefer the diatonic spelling when the exercise offers it, fall back
  // to the alternate. PREFERRED_INTERVAL_BY_SEMITONE[6] = "A4"; "d5" is
  // accepted only when "A4" is not in the option set.
  const preferred = PREFERRED_INTERVAL_BY_SEMITONE[simple];
  if (preferred && exercise.options.some((o) => o.id === preferred)) {
    return { optionId: preferred };
  }
  for (const alias of aliases) {
    if (exercise.options.some((o) => o.id === alias)) {
      return { optionId: alias };
    }
  }
  return null;
}

/* ─── Chord-quality + chord-inversion grading ────────────────────────── */

export interface MidiNoteWithTimestamp {
  midi: number;
  timestamp: number;
}

export interface MidiChordResult {
  optionId: string;
  /** The identified chord, exposed for diagnostics / future UI readouts. */
  chord: IdentifiedChord;
}

const DEFAULT_SIMULTANEITY_WINDOW_MS = 100;

export interface ChordGradingOptions {
  /**
   * Notes outside this window from the most recent noteon are ignored.
   * Per spec §10.3, the default is 100 ms, which is comfortable for a
   * pianist striking a chord and tight enough to reject sequential
   * note presses bleeding into the next chord.
   */
  simultaneityWindowMs?: number;
}

/**
 * Filter a recent-noteon stream to the simultaneity window. Returns the
 * MIDI numbers of notes whose timestamps are within `windowMs` of the
 * most recent note. Empty input → empty array.
 */
function notesInSimultaneityWindow(
  notes: ReadonlyArray<MidiNoteWithTimestamp>,
  windowMs: number,
): number[] {
  if (notes.length === 0) return [];
  let latestTs = -Infinity;
  for (const n of notes) {
    if (n.timestamp > latestTs) latestTs = n.timestamp;
  }
  const cutoff = latestTs - windowMs;
  const out: number[] = [];
  for (const n of notes) {
    if (n.timestamp >= cutoff) out.push(n.midi);
  }
  return out;
}

/**
 * Map a set of recent MIDI noteons → option ID for a chord-quality
 * exercise. Triads + 7ths only (per Phase 2A C.2 scope); extended +
 * altered qualities return null and Practice will leave the answer
 * un-submitted so the user can click instead.
 *
 * Calls into `identifyChord` for the note-set → quality logic, then
 * looks up the matching option ID from `exercise.options`. The
 * generator's option IDs are quality strings (`maj`, `min7`, `dom7`,
 * etc.) so the lookup is a direct equality check.
 */
export function gradeMidiChordQuality(
  notes: ReadonlyArray<MidiNoteWithTimestamp>,
  exercise: Exercise,
  opts?: ChordGradingOptions,
): MidiChordResult | null {
  if (exercise.exerciseType !== "chord-quality-recognition") return null;
  const window = opts?.simultaneityWindowMs ?? DEFAULT_SIMULTANEITY_WINDOW_MS;
  const midiSet = notesInSimultaneityWindow(notes, window);
  const chord = identifyChord(midiSet);
  if (!chord) return null;
  const optionId = chord.quality;
  if (!exercise.options.some((o) => o.id === optionId)) return null;
  return { optionId, chord };
}

/**
 * Map a set of recent MIDI noteons → option ID for a chord-inversion
 * exercise. Same chord-id pipeline as the quality grader but the option
 * ID encodes the inversion (`inv-0`, `inv-1`, `inv-2`).
 *
 * The chord-inversion generator currently uses triads only; if a future
 * extension adds 7th-chord inversions, `inv-3` would also be a valid
 * lookup target — `identifyChord` already returns inversion values up
 * to 3 for sevenths, so no code change is needed here.
 */
export function gradeMidiChordInversion(
  notes: ReadonlyArray<MidiNoteWithTimestamp>,
  exercise: Exercise,
  opts?: ChordGradingOptions,
): MidiChordResult | null {
  if (exercise.exerciseType !== "chord-inversion-recognition") return null;
  const window = opts?.simultaneityWindowMs ?? DEFAULT_SIMULTANEITY_WINDOW_MS;
  const midiSet = notesInSimultaneityWindow(notes, window);
  const chord = identifyChord(midiSet);
  if (!chord) return null;
  const optionId = `inv-${chord.inversion}`;
  if (!exercise.options.some((o) => o.id === optionId)) return null;
  return { optionId, chord };
}

/* ─── Melodic-dictation sequence (Block D.1 helper) ──────────────────── */

export interface MidiSequenceResult {
  /**
   * Octave-agnostic scale-degree sequence: each entry is `{ degree,
   * alteration? }` from `@music-app/theory-engine`. D.1's
   * `gradeDictation()` consumes this directly — same shape, no shim.
   */
  scaleDegrees: ScaleDegreeSequence;
}

/**
 * Map a sequence of MIDI noteons → scale-degree sequence in a key.
 *
 * The `melodic-dictation` exercise type was added in Block D.1 and
 * Practice's grading dispatcher consumes this helper directly. The
 * mapping function lives in `@music-app/theory-engine`'s
 * `midiToScaleDegree` so the same shape is shared with the
 * `<DictationInput>` UI's piano-mode entry.
 *
 * Returns null if the key context is missing from the exercise meta or
 * any note doesn't map to a (possibly chromatic) scale degree. Always
 * resolves chromatic notes by trying the diatonic mapping first, then
 * the ±1 chromatic neighbors with the raised spelling preferred (same
 * as `@music-app/input-mic`'s `gradeScaleDegree({ allowChromatic: true })`).
 */
export function gradeMidiSequence(
  noteons: ReadonlyArray<{ midi: number }>,
  exercise: Exercise,
): MidiSequenceResult | null {
  const tonicMidi = exercise.meta.tonicMidi;
  const mode = exercise.meta.tonalityMode;
  if (typeof tonicMidi !== "number" || (mode !== "major" && mode !== "minor")) {
    return null;
  }
  const out: { degree: number; alteration?: "♯" | "♭" }[] = [];
  for (const { midi } of noteons) {
    const sd = theoryMidiToScaleDegree(midi, tonicMidi, mode, { allowChromatic: true });
    if (sd === null) return null;
    // Strip octaveOffset — gradeMidiSequence is octave-agnostic by
    // design (gradeDictation handles octave-strict comparison via its
    // own option).
    if (sd.alteration !== undefined) {
      out.push({ degree: sd.degree, alteration: sd.alteration });
    } else {
      out.push({ degree: sd.degree });
    }
  }
  return { scaleDegrees: out };
}
